// editorSlice.js - Redux slice for editor UI and interactions
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { editorAPI } from "./api";
import { getOriginalTime } from "../originalTime";
import { createMiddleware } from "../middlewareUtil";

// Initial state
const initialState = {
  // Audio state
  audio: {
    guid: "",
    isLoaded: false,
    isLoading: false,
    clipId: 0,
    commitId: 0,
    channel: 0,
  },

  // Playback state
  playback: {
    clipTime: 0,
    originalTime: 0,
  },

  // User preferences
  preferences: {
    waveOpacity: 0.25,
    zoomAmount: 0,
    fftSamples: 1024,
    fftFrequencies: {
      min: 0,
      max: 20000,
    },
    selectedChannel: 1,
    isLoading: false,
    error: null,
  },

  // Selection state
  selection: {
    selectedAnnotation: null,
    selectedTag: null,
  },

  // Operations state
  operations: {
    isTagging: false,
    isFiltering: false,
    error: null,
  },

  // General state
  error: null,
};

/**
 * Load user preferences
 */
export const loadPreferences = createAsyncThunk(
  "editor/loadPreferences",
  async ({ caseId, clipId }, { getState, rejectWithValue }) => {
    const { accessCode } = getState().auth;

    try {
      const result = await editorAPI.loadPreferences(
        accessCode,
        caseId,
        clipId
      );

      if (!result.success) {
        return rejectWithValue({
          error: result.error || "Failed to load preferences",
        });
      }

      return result.data || cloneDeep(initialState.preferences);
    } catch (error) {
      console.error("[editor/loadPreferences] Error:", error);
      return rejectWithValue({
        error: error.message || "Failed to load preferences",
      });
    }
  }
);

/**
 * Save user preferences
 */
export const savePreferences = createAsyncThunk(
  "editor/savePreferences",
  async ({ caseId, clipId, preferences }, { getState, rejectWithValue }) => {
    const { accessCode } = getState().auth;

    try {
      const result = await editorAPI.savePreferences(
        accessCode,
        caseId,
        clipId,
        preferences
      );

      if (!result.success) {
        return rejectWithValue({
          error: result.error || "Failed to save preferences",
        });
      }

      return preferences;
    } catch (error) {
      console.error("[editor/savePreferences] Error:", error);
      return rejectWithValue({
        error: error.message || "Failed to save preferences",
      });
    }
  }
);

/**
 * Load audio GUID for playback
 */
export const loadAudioGUID = createAsyncThunk(
  "editor/loadAudioGUID",
  async ({ clipId, commitId, channel }, { getState, rejectWithValue }) => {
    const { accessCode } = getState().auth;

    try {
      const result = await editorAPI.loadAudioGUID(accessCode, clipId, channel);

      if (!result.success) {
        return rejectWithValue({
          error: result.error || "Failed to load audio GUID",
        });
      }

      return {
        guid: result.data?.value || "",
        clipId,
        commitId,
        channel,
      };
    } catch (error) {
      console.error("[editor/loadAudioGUID] Error:", error);
      return rejectWithValue({
        error: error.message || "Failed to load audio GUID",
      });
    }
  }
);

/**
 * Request filter operation
 */
export const requestFilter = createAsyncThunk(
  "editor/requestFilter",
  async (
    { caseId, clipId, filterType, comment },
    { getState, rejectWithValue }
  ) => {
    const { accessCode } = getState().auth;
    const { annotations } = getState().annotations;

    try {
      const filteredAnnotations = annotations.filter(
        (a) => a.tag === getState().editor.selection.selectedTag
      );
      if (!filteredAnnotations.length) {
        return rejectWithValue({
          error: "No annotations found for the selected tag",
        });
      }
      const result = await editorAPI.requestFilter(
        accessCode,
        caseId,
        clipId,
        filterType,
        filteredAnnotations,
        comment
      );

      if (!result.success) {
        return rejectWithValue({
          error: result.error || "Failed to process filter request",
        });
      }

      return {
        filterType,
        annotations: filteredAnnotations,
        result: result.data,
      };
    } catch (error) {
      console.error("[editor/requestFilter] Error:", error);
      return rejectWithValue({
        error: error.message || "Failed to process filter request",
      });
    }
  }
);

// Create the slice
const editorSlice = createSlice({
  name: "editor",
  initialState,
  reducers: {
    // Reset to initial state
    reset: () => cloneDeep(initialState),

    // Update preferences locally
    updatePreferences: (state, action) => {
      state.preferences = {
        ...state.preferences,
        ...action.payload,
      };
    },

    // Set playback time
    setPlaybackTime: (state, action) => {
      const { clipTime, clip } = action.payload;
      let originalTime = clipTime;

      if (clip?.timeMappings) {
        originalTime = getOriginalTime(clip.timeMappings, clipTime);
      }

      state.playback.clipTime = clipTime;
      state.playback.originalTime = originalTime;
    },

    startTagging: (state) => {
      state.operations.isTagging = true;
    },
    stopTagging: (state) => {
      state.operations.isTagging = false;
    },
    // Select annotation
    selectAnnotation: (state, action) => {
      const { annotationId, tag } = action.payload;

      // If clicking the same annotation with a tag, deselect
      if (
        state.selection.selectedAnnotation === annotationId &&
        tag &&
        tag === state.selection.selectedTag
      ) {
        state.selection.selectedAnnotation = null;
        state.selection.selectedTag = null;
      } else {
        state.selection.selectedAnnotation = annotationId;
        state.selection.selectedTag = tag;
      }
    },

    // Select by tag
    selectTag: (state, action) => {
      const tag = action.payload;

      // If clicking the same tag, deselect
      if (state.selection.selectedTag === tag) {
        state.selection.selectedTag = null;
        state.selection.selectedAnnotation = null;
      } else {
        state.selection.selectedTag = tag;
        state.selection.selectedAnnotation = null;
      }
    },

    // Clear all selections
    clearSelection: (state) => {
      state.selection.selectedAnnotation = null;
      state.selection.selectedTag = null;
    },

    // Clear audio state
    clearAudio: (state) => {
      state.audio = {
        guid: "",
        isLoaded: false,
        isLoading: false,
        clipId: 0,
        commitId: 0,
        channel: 0,
      };
    },

    // Clear errors
    clearError: (state) => {
      state.error = null;
      state.preferences.error = null;
      state.operations.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // Load preferences
      .addCase(loadPreferences.pending, (state) => {
        state.preferences.isLoading = true;
        state.preferences.error = null;
      })
      .addCase(loadPreferences.fulfilled, (state, action) => {
        state.preferences.isLoading = false;
        state.preferences = {
          ...state.preferences,
          ...action.payload,
        };
      })
      .addCase(loadPreferences.rejected, (state, action) => {
        state.preferences.isLoading = false;
        state.preferences.error =
          action.payload?.error || "Failed to load preferences";
      })

      // Save preferences
      .addCase(savePreferences.pending, (state) => {
        state.preferences.isLoading = true;
        state.preferences.error = null;
      })
      .addCase(savePreferences.fulfilled, (state, action) => {
        state.preferences.isLoading = false;
        // Preferences are already updated from the action payload
      })
      .addCase(savePreferences.rejected, (state, action) => {
        state.preferences.isLoading = false;
        state.preferences.error =
          action.payload?.error || "Failed to save preferences";
      })

      // Load audio GUID
      .addCase(loadAudioGUID.pending, (state) => {
        state.audio.isLoading = true;
        state.audio.isLoaded = false;
        state.error = null;
      })
      .addCase(loadAudioGUID.fulfilled, (state, action) => {
        state.audio.isLoading = false;
        state.audio.isLoaded = true;
        state.audio.guid = action.payload.guid;
        state.audio.clipId = action.payload.clipId;
        state.audio.commitId = action.payload.commitId;
        state.audio.channel = action.payload.channel;
      })
      .addCase(loadAudioGUID.rejected, (state, action) => {
        state.audio.isLoading = false;
        state.audio.isLoaded = false;
        state.error = action.payload?.error || "Failed to load audio";
      })

      // Request filter
      .addCase(requestFilter.pending, (state) => {
        state.operations.isFiltering = true;
        state.operations.error = null;
      })
      .addCase(requestFilter.fulfilled, (state, action) => {
        state.operations.isFiltering = false;
        console.log("Filter request completed:", action.payload);
      })
      .addCase(requestFilter.rejected, (state, action) => {
        state.operations.isFiltering = false;
        state.operations.error =
          action.payload?.error || "Filter request failed";
      });
  },
});

export const editorMiddleware = createMiddleware([
  "editor/loadPreferences/pending",
  "editor/savePreferences/pending",
  "editor/loadAudioGUID/pending",
  "editor/requestFilter/pending",
]);

// Export actions
export const {
  reset,
  updatePreferences,
  setPlaybackTime,
  selectAnnotation,
  selectTag,
  clearSelection,
  clearAudio,
  clearError,
} = editorSlice.actions;

// Export reducer
export default editorSlice.reducer;
